import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useMutation, useQuery } from "react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { MdError } from "react-icons/md";

import { Input } from "../../components/Input";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import RestaurantInvoice from "../../components/restaurant/invoice";
import { FaRegCheckCircle } from "react-icons/fa";

const UserInvoice = () => {
  const [otp, setOtp] = useState<string[]>(Array(4).fill(""));
  const [isOtp, setIsOtp] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isDone, setDone] = useState<boolean>(false);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const restId = searchParams.get("restId");
  const deviceId = searchParams.get("deviceId");

  const {
    data: invoice,
    isLoading,
    isError,
    error,
  } = useQuery(
    "Get Invoice",
    async () => {
      const response = await axios.get("/order/foodics/web/last-order", {
        params: {
          restaurantId: restId,
          casherId: deviceId,
        },
      });
      return response.data.invoice;
    },
    { retry: 1, refetchOnWindowFocus: false }
  );

  const { mutate: addUser, isLoading: addUserLoading } = useMutation(
    async (data: any) => {
      const res = await axios.post("/user", data);
      if (res.status === 200) setIsOtp(true);
      return res.data;
    }
  );

  const { mutateAsync: sendOTP, isLoading: sendOTPLoading } = useMutation(
    async (data: any) => {
      const res = await axios.post("/order/foodics/web/add-order", data);

      if (res.status === 200) {
        setIsSaved(false);
        setIsOtp(false);
        setOtp(Array(6).fill(""));
        inputs.current.forEach((input) => {
          if (input) input.value = "";
        });
        formik.resetForm();
        setDone(true);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Your invoice has been saved successfully.",
          confirmButtonText: "Done",
          confirmButtonColor: "#21D868",
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            title: "swal2-title-custom",
            htmlContainer: "swal2-text-custom",
          },
          didOpen: () => {
            const titleEl = document.querySelector(".swal2-title");
            if (titleEl) titleEl.setAttribute("style", "color: #21D868");
            const textEl = document.querySelector(".swal2-html-container");
            if (textEl) textEl.setAttribute("style", "color: #94A3B8;");
          },
        });
      }
      return res.data;
    }
  );

  const formik = useFormik({
    initialValues: {
      phone: "",
    },
    validationSchema: Yup.object({
      phone: Yup.string().required("Phone is required"),
    }),
    onSubmit: async (values) => {
      try {
        addUser({ phone: values.phone });
      } catch (error) {
        console.log(error);
      }
    },
  });

  const handleSendOTP = async () => {
    try {
      await sendOTP({
        casherId: deviceId,
        restaurantId: restId,
        phone: formik.values.phone,
        otp: otp.join(""),
      });
    } catch (error: any) {
      setIsSaved(false);
      setIsOtp(false);
      setOtp(Array(4).fill(""));
      inputs.current.forEach((input) => {
        if (input) input.value = "";
      });

      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: error?.response?.data?.message || "Something went wrong.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#FE5960",
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          title: "swal2-title-custom",
          htmlContainer: "swal2-text-custom",
        },
        didOpen: () => {
          const titleEl = document.querySelector(".swal2-title");
          if (titleEl) titleEl.setAttribute("style", "color: #FE5960");
          const textEl = document.querySelector(".swal2-html-container");
          if (textEl) textEl.setAttribute("style", "color: #94A3B8;");
        },
      });
    }
  };

  const handleChangeOtp = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && inputs.current[index + 1]) {
      setTimeout(() => {
        inputs.current[index + 1]?.focus();
      }, 10);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const paste = e.clipboardData.getData("text").slice(0, 4);
    if (!/^\d{1,4}$/.test(paste)) return;

    const newOtp = paste.split("");
    setOtp((prev) => {
      const filled = [...prev];
      for (let i = 0; i < 4; i++) {
        filled[i] = newOtp[i] || "";
        if (inputs.current[i]) {
          inputs.current[i]!.value = newOtp[i] || "";
        }
      }
      return filled;
    });

    setTimeout(() => {
      inputs.current[paste.length]?.focus();
    }, 10);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        setOtp((prev) => {
          const newOtp = [...prev];
          newOtp[index - 1] = "";
          return newOtp;
        });
        inputs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < otp.length - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  useEffect(() => {
    document.title = "Specter | Invoice";
  }, []);

  if (isLoading || isError)
    return (
      <div
        dir="ltr"
        className="w-full min-h-screen flex justify-center md:items-center p-4 md:p-0 overflow-auto"
      >
        <div className="flex flex-col justify-center items-center w-full md:!w-96  h-fit p-6 rounded-2xl md:bg-gray-100 md:shadow-xl shadow-black my-4 md:my-10">
          <img src="/splash-logo.png" alt="Logo" className="h-8 mb-4" />
          <h1 className="text-xl font-bold pt-4">Welcome to Specter</h1>
          {isLoading ? (
            <div className="flex justify-center items-center py-40">
              <Loader />
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center rounded-lg p-6 shadow shadow-red-500 bg-red-100 text-red-500 gap-4 my-10">
              <MdError className="text-5xl" />
              <p className="text-xl font-bold">Oops!</p>
              <p className="font-semibold">
                {typeof error === "object" &&
                error !== null &&
                "response" in error &&
                (error as any).response?.data?.message
                  ? (error as any).response.data.message + "."
                  : "An error occurred"}
              </p>
            </div>
          )}
        </div>
      </div>
    );

  return (
    <div
      dir="ltr"
      className="w-full min-h-screen flex justify-center md:items-center p-4 md:p-0 overflow-auto"
    >
      {isDone ? (
        <div className="flex flex-col justify-center items-center w-fit h-fit mt-10 p-8 md:p-20 space-y-6 rounded-2xl md:bg-gray-100 md:shadow-xl shadow-black">
          <img src="/splash-logo.png" alt="Logo" />
          <FaRegCheckCircle className="text-7xl md:text-8xl text-primary/80" />

          <p className="text-primary text-center text-xl font-semibold mt-4">
            Your invoice has been saved successfully
          </p>
        </div>
      ) : !isSaved ? (
        <RestaurantInvoice
          invoice={invoice}
          onSave={() => setIsSaved(true)}
          type="web"
        />
      ) : (
        <div className="flex flex-col justify-center items-center w-fit h-fit mt-10 p-8 md:p-20 space-y-6 rounded-2xl md:bg-gray-100 md:shadow-xl shadow-black">
          <img src="/splash-logo.png" alt="Logo" />
          <h1 className="text-2xl font-bold">Welcome to Specter</h1>

          {!isOtp ? (
            <>
              <p className="text-gray-600 text-center  font-medium mb-6">
                Please Enter your Phone Number to save your invoice.
              </p>
              <form
                onSubmit={formik.handleSubmit}
                className="w-full first-letter:flex flex-col gap-1 transition-all"
              >
                <label className="text-left w-full text-sm text-green-500 font-semibold">
                  Phone Number
                </label>
                <Input
                  type="text"
                  className="w-full md:w-96 h-10 border-2 border-green-500 text-gray-600 font-medium"
                  placeholder="Enter your phone number"
                  required
                  {...formik.getFieldProps("phone")}
                  error={formik.touched.phone ? formik.errors.phone : ""}
                />
                <Button
                  className="w-full rounded-lg text-md py-2 mt-6 !bg-primary !text-white !font-bold focus:outline-none focus:ring-0 hover:!bg-primary/80"
                  type="submit"
                  disabled={
                    !formik.values.phone ||
                    formik.isSubmitting ||
                    addUserLoading
                  }
                  loading={addUserLoading}
                >
                  Save
                </Button>
              </form>
            </>
          ) : (
            <>
              <p className="text-gray-600 text-center w-full font-medium mb-6">
                Please Enter the OTP sent to your phone number to save your
                invoice.
              </p>
              <div className="w-fit flex flex-col gap-1 transition-all">
                <label className="text-left w-full text-sm text-green-500 font-semibold">
                  OTP
                </label>
                <div className="w-fit flex justify-start items-center gap-4">
                  {otp.map((digit, idx) => (
                    <Input
                      key={idx}
                      ref={(el: HTMLInputElement | null) =>
                        (inputs.current[idx] = el)
                      }
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChangeOtp(idx, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      onPaste={handlePaste}
                      autoFocus={idx === 0}
                      className="w-14 md:w-20 h-10 md:h-12 border text-center text-xl text-gray-600 font-medium"
                    />
                  ))}
                </div>
                <Button
                  className="w-full rounded-lg text-md py-2 mt-6 !bg-primary !text-white !font-bold focus:outline-none focus:ring-0 hover:!bg-primary/80"
                  type="submit"
                  loading={sendOTPLoading}
                  disabled={
                    otp.every((digit) => digit === "") || sendOTPLoading
                  }
                  onClick={handleSendOTP}
                >
                  Send
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UserInvoice;
